import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Grid,
  Divider,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import { useNavigate } from 'react-router-dom';

interface FieldMetadata {
  field: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'multiselect' | 'json' | 'datetime' | 'user-reference';
  required?: boolean;
  options?: string[] | { value: string; label: string }[];
  placeholder?: string;
  helperText?: string;
  defaultValue?: any;
  min?: number;
  max?: number;
  multiline?: boolean;
  rows?: number;
  referenceEntity?: string; // For reference fields (e.g., 'user')

  // Organization & Metadata
  section?: string;        // Section name for grouping fields
  category?: string;       // Category for finer grouping within a section
  tags?: string[];         // Flexible tags for categorization
  order?: number;          // Display order within section
  description?: string;    // Longer description for documentation
  hidden?: boolean;        // Whether to hide this field by default
  readOnly?: boolean;      // Whether this field is read-only
}

interface EntityCreatorMetadata {
  displayName: string;
  singularName: string;
  fields: FieldMetadata[];
  endpoint: string; // POST endpoint to create entity
}

interface GenericEntityCreatorProps {
  entityType: string;
  onSuccess?: (entity: any) => void;
  onCancel?: () => void;
  redirectRoute?: string; // Route to redirect after creation
}

const GenericEntityCreator: React.FC<GenericEntityCreatorProps> = ({
  entityType,
  onSuccess,
  onCancel,
  redirectRoute,
}) => {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Record<string, any>>({});

  // Fetch metadata for the entity type
  const { data: metadata, isLoading: metadataLoading } = useQuery<EntityCreatorMetadata>({
    queryKey: ['entity-creator-metadata', entityType],
    queryFn: async () => {
      const response = await api.get(`/entities/${entityType}/create-metadata`);
      return response;
    },
  });

  // Fetch users for user-reference fields
  const { data: usersData } = useQuery({
    queryKey: ['users-for-reference', userSearchQuery],
    queryFn: async () => {
      const response = await api.get(`/users?search=${userSearchQuery}&limit=50`);
      return response;
    },
    enabled: metadata?.fields.some(f => f.type === 'user-reference') || false,
  });

  const users = usersData?.users || [];

  // Group fields by section - MUST be before early returns to avoid hooks order violation
  const groupedFields = React.useMemo(() => {
    if (!metadata) return {};

    const groups: Record<string, FieldMetadata[]> = {};

    metadata.fields.forEach((field) => {
      const section = field.section || 'General';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(field);
    });

    // Sort fields within each section by order
    Object.keys(groups).forEach((section) => {
      groups[section].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return groups;
  }, [metadata]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const response = await api.post(metadata!.endpoint, data);
      return response;
    },
    onSuccess: (data) => {
      // Invalidate entity list query
      queryClient.invalidateQueries({ queryKey: ['entities', entityType] });
      
      if (onSuccess) {
        onSuccess(data);
      }
      
      if (redirectRoute) {
        navigate(redirectRoute);
      }
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.error || 'Failed to create entity');
    },
  });

  // Initialize form data with default values
  React.useEffect(() => {
    if (metadata) {
      const initialData: Record<string, any> = {};
      metadata.fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          initialData[field.field] = field.defaultValue;
        } else if (field.type === 'boolean') {
          initialData[field.field] = false;
        } else if (field.type === 'multiselect') {
          initialData[field.field] = [];
        } else if (field.type === 'json') {
          initialData[field.field] = {};
        } else {
          initialData[field.field] = '';
        }
      });
      setFormData(initialData);
    }
  }, [metadata]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    if (!metadata) return false;

    const newErrors: Record<string, string> = {};

    metadata.fields.forEach((field) => {
      const value = formData[field.field];

      // Check required fields
      if (field.required) {
        if (value === undefined || value === null || value === '') {
          newErrors[field.field] = `${field.label} is required`;
        }
      }

      // Validate number ranges
      if (field.type === 'number' && value !== '' && value !== null && value !== undefined) {
        const numValue = Number(value);
        if (field.min !== undefined && numValue < field.min) {
          newErrors[field.field] = `${field.label} must be at least ${field.min}`;
        }
        if (field.max !== undefined && numValue > field.max) {
          newErrors[field.field] = `${field.label} must be at most ${field.max}`;
        }
      }

      // Validate JSON fields
      if (field.type === 'json' && typeof value === 'string' && value.trim() !== '') {
        try {
          JSON.parse(value);
        } catch (e) {
          newErrors[field.field] = 'Invalid JSON format';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    // Process form data before submission
    const processedData: Record<string, any> = {};
    
    metadata!.fields.forEach((field) => {
      let value = formData[field.field];

      // Convert JSON strings to objects
      if (field.type === 'json' && typeof value === 'string') {
        try {
          value = value.trim() === '' ? {} : JSON.parse(value);
        } catch (e) {
          value = {};
        }
      }

      // Convert number strings to numbers
      if (field.type === 'number' && value !== '' && value !== null) {
        value = Number(value);
      }

      processedData[field.field] = value;
    });

    createMutation.mutate(processedData);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (redirectRoute) {
      navigate(redirectRoute);
    }
  };

  const renderField = (field: FieldMetadata) => {
    const value = formData[field.field] ?? '';
    const error = errors[field.field];

    switch (field.type) {
      case 'user-reference':
        const selectedUser = selectedUsers[field.field];
        return (
          <Autocomplete
            options={users}
            getOptionLabel={(option: any) => option.email || ''}
            value={selectedUser || null}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedUsers({ ...selectedUsers, [field.field]: newValue });
                handleFieldChange(field.field, newValue.id);
              } else {
                setSelectedUsers({ ...selectedUsers, [field.field]: null });
                handleFieldChange(field.field, '');
              }
            }}
            onInputChange={(event, newInputValue) => {
              setUserSearchQuery(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                required={field.required}
                error={Boolean(error)}
                helperText={error || field.helperText}
                placeholder={field.placeholder || 'Search users by email...'}
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.name || 'No name'} • ID: {option.id.substring(0, 8)}...
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            loading={false}
            fullWidth
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => handleFieldChange(field.field, e.target.checked)}
              />
            }
            label={field.label}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth error={Boolean(error)} required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(field.field, e.target.value)}
              label={field.label}
            >
              {field.options?.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {field.helperText && !error && (
              <Typography variant="caption" color="text.secondary">{field.helperText}</Typography>
            )}
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl fullWidth error={Boolean(error)} required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => handleFieldChange(field.field, e.target.value)}
              label={field.label}
            >
              {field.options?.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                return (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {field.helperText && !error && (
              <Typography variant="caption" color="text.secondary">{field.helperText}</Typography>
            )}
          </FormControl>
        );

      case 'json':
        return (
          <TextField
            fullWidth
            label={field.label}
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={(e) => handleFieldChange(field.field, e.target.value)}
            multiline
            rows={field.rows || 4}
            placeholder={field.placeholder || '{}'}
            helperText={error || field.helperText}
            error={Boolean(error)}
            required={field.required}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.field, e.target.value)}
            placeholder={field.placeholder}
            helperText={error || field.helperText}
            error={Boolean(error)}
            required={field.required}
            inputProps={{
              min: field.min,
              max: field.max,
            }}
          />
        );

      default: // string, datetime
        return (
          <TextField
            fullWidth
            type={field.type === 'datetime' ? 'datetime-local' : 'text'}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.field, e.target.value)}
            multiline={field.multiline}
            rows={field.multiline ? field.rows || 3 : undefined}
            placeholder={field.placeholder}
            helperText={error || field.helperText}
            error={Boolean(error)}
            required={field.required}
          />
        );
    }
  };

  if (metadataLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!metadata) {
    return (
      <Alert severity="error">
        Failed to load creation form for {entityType}
      </Alert>
    );
  }

  const sectionOrder = Object.keys(groupedFields);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Create New {metadata.singularName}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {sectionOrder.map((sectionName, sectionIndex) => (
        <Box key={sectionName} sx={{ mb: 4 }}>
          {/* Section Header */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              mt: sectionIndex > 0 ? 3 : 0,
              color: 'primary.main',
              fontWeight: 600,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              pb: 1,
            }}
          >
            {sectionName}
          </Typography>

          {/* Section Fields */}
          <Grid container spacing={3}>
            {groupedFields[sectionName].map((field) => (
              <Grid
                item
                xs={12}
                md={field.type === 'json' || field.multiline ? 12 : 6}
                key={field.field}
              >
                <Box>
                  {renderField(field)}
                  {/* Show description if available */}
                  {field.description && !field.helperText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {field.description}
                    </Typography>
                  )}
                  {/* Show tags if available (for debugging/admin view) */}
                  {field.tags && field.tags.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {field.tags.map((tag) => (
                        <Typography
                          key={tag}
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.25,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                          }}
                        >
                          {tag}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Divider sx={{ my: 3 }} />

      <Box display="flex" gap={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<Cancel />}
          onClick={handleCancel}
          disabled={createMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={createMutation.isPending ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSubmit}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : `Create ${metadata.singularName}`}
        </Button>
      </Box>
    </Paper>
  );
};

export default GenericEntityCreator;

